/// <reference lib="DOM" />

import type { FileUIPart } from "ai";
import { FileText, Paperclip } from "lucide-react";

interface ChatFilePartProps {
  part: FileUIPart;
}

/**
 * A non-image attachment in the transcript, as an openable chip.
 *
 * Images get a thumbnail (`ChatImagePart`); a PDF has nothing to show inline, so
 * the bubble carries its name and opens it in a new tab. The `url` is whatever
 * reference the host's uploader returned — a same-origin, cookie-authed one
 * makes a plain anchor the whole mechanism.
 *
 * Falls back to a generic label rather than hiding the part: an attachment the
 * member sent must be visible in their own transcript even when we don't
 * recognize its type.
 */
export function ChatFilePart({ part }: ChatFilePartProps) {
  const isPdf = baseMediaType(part.mediaType) === "application/pdf";
  const Icon = isPdf ? FileText : Paperclip;
  const label = part.filename ?? (isPdf ? "PDF" : "Attachment");

  return (
    <a
      href={part.url}
      target="_blank"
      rel="noreferrer"
      title={label}
      className="inline-flex max-w-full items-center gap-1.5 rounded-md border bg-muted/40 px-2 py-1.5 text-xs text-foreground no-underline transition-colors hover:bg-muted"
    >
      <Icon className="size-3.5 shrink-0 text-muted-foreground" />
      <span className="truncate">{label}</span>
    </a>
  );
}

/** Drop any `; charset=…` parameters so a media type compares by value. */
function baseMediaType(mediaType: string): string {
  return mediaType.split(";")[0]!.trim().toLowerCase();
}
